/**
 * The small pieces every screen draws the same way: a link that stays in the
 * page, a state pill that carries an icon and a word as well as a colour, the
 * worker's refusal in its own words, "still loading", the top of a screen and
 * an empty list.
 */

import { useEffect, useState, type MouseEvent, type ReactNode } from "react";
import type { SlaStatus, TaskState } from "@utuwrk/core";
import { follow, pathOf, type Screen } from "../lib/router.js";
import { SLA_CLASS, SLA_LABEL, STATE_CLASS, STATE_LABEL } from "../lib/words.js";
import { Icon, type IconName } from "./Icon.js";

/**
 * A link that stays in the page. `instead`, when given, is what a plain click
 * does in place of moving (My work opens a card beside the list); a modified
 * click still opens the address in a tab.
 */
export function Link({
  to,
  className,
  children,
  current,
  instead,
}: {
  to: Screen;
  className?: string | undefined;
  children: ReactNode;
  current?: boolean | "true" | undefined;
  instead?: (() => void) | undefined;
}) {
  const click = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!instead) return follow(to)(event);
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
    event.preventDefault();
    instead();
  };
  return (
    <a
      href={pathOf(to)}
      onClick={click}
      className={className}
      aria-current={current === "true" ? "true" : current ? "page" : undefined}
    >
      {children}
    </a>
  );
}

const STATE_ICON: Record<TaskState, string> = {
  not_started: "M6 3.2V6l1.8 1.2",
  unblocked: "M4.5 3l3.5 3-3.5 3",
  in_progress: "M3 6h6M6 3l3 3-3 3",
  blocked: "M3 3l6 6M9 3l-6 6",
  completed: "M2.8 6.2l2.2 2.2 4.2-4.6",
  skipped: "M3 6h6",
};

export function StatePill({ state }: { state: TaskState }) {
  return (
    <span className={`pill ${STATE_CLASS[state]}`}>
      <svg viewBox="0 0 12 12" aria-hidden="true" className="pill-icon">
        {state === "not_started" ? <circle cx="6" cy="6" r="4.5" /> : null}
        <path d={STATE_ICON[state]} />
      </svg>
      {STATE_LABEL[state]}
    </span>
  );
}

export function SlaPill({ sla }: { sla: SlaStatus }) {
  const label = SLA_LABEL[sla];
  if (!label) return null;
  return (
    <span className={`pill ${SLA_CLASS[sla]}`}>
      <svg viewBox="0 0 12 12" aria-hidden="true" className="pill-icon">
        <circle cx="6" cy="6" r="4.5" />
        <path d="M6 3.5v3M6 8.4v.1" />
      </svg>
      {label}
    </span>
  );
}

export function Problem({ children }: { children: ReactNode }) {
  if (!children) return null;
  return (
    <p className="problem" role="alert">
      {children}
    </p>
  );
}

export function Loading({ title }: { title: string }) {
  return (
    <section className="page" aria-busy="true">
      <PageHeader title={title} meta="Loading…" />
    </section>
  );
}

/**
 * The top of every screen, as utucat draws it: where you are, the way back,
 * a line of facts, and the buttons that act on the whole screen. On the page
 * itself, not in a card, so the title, the way back and the buttons sit in
 * the same place everywhere. No subtitle that repeats what the screen's own
 * controls already say.
 */
export function PageHeader({
  title,
  eyebrow,
  back,
  meta,
  actions,
}: {
  title: ReactNode;
  eyebrow?: ReactNode;
  back?: { to: Screen; label: string } | undefined;
  meta?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <header className="page-head">
      {back ? (
        <Link to={back.to} className="back-link">
          <Icon name="chevron-left" size={16} />
          {back.label}
        </Link>
      ) : null}
      {eyebrow ? <p className="eyebrow">{eyebrow}</p> : null}
      <div className="page-head-row">
        <div className="page-head-text">
          <h1 className="page-title">{title}</h1>
          {meta ? <div className="page-meta">{meta}</div> : null}
        </div>
        {actions ? <div className="page-actions">{actions}</div> : null}
      </div>
    </header>
  );
}

/** What a list says when it has nothing in it: an icon, one line, and what to do next if there is anything. */
export function EmptyState({
  icon,
  title,
  children,
  action,
}: {
  icon: IconName;
  title: string;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="empty">
      <span className="empty-icon">
        <Icon name={icon} size={22} />
      </span>
      <p className="empty-title">{title}</p>
      {children ? <p className="empty-body">{children}</p> : null}
      {action}
    </div>
  );
}

/**
 * Load something once a screen opens, with the refusal kept as a sentence.
 * `reload` is for after a write that did not answer with the new state.
 */
export function useLoad<T>(load: () => Promise<T>, deps: unknown[]) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let live = true;
    setError(null);
    load().then(
      (value) => live && setData(value),
      (problem: unknown) => live && setError(problem instanceof Error ? problem.message : String(problem)),
    );
    return () => {
      live = false;
    };
  }, [...deps, tick]);

  return { data, setData, error, setError, reload: () => setTick((value) => value + 1) };
}
